import React, {useMemo, useState} from 'react';
import { useTranslation } from "react-i18next";
import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { Typography } from "@mui/joy";
import { GridViewRounded, ViewListRounded } from "@mui-symbols-material/w300";
import { FileListViewMode, loadFileListViewMode, saveFileListViewMode } from "../../utils/fileListViewMode";
import {SystemFiles} from "../../graphql/queries/systemFiles";
import {useMutation, useQuery} from "@apollo/client";
import {AddFilesToInstalledBaseNode} from "../../graphql/mutations/addFilesToInstalledBaseNode";
import _ from "lodash";
import {DropzoneArea, Error, uploadFilesRequest, WidgetBox} from "@dreebit-registry/vsm-microservice-components";
import {resolveErrorMessage, sanitizeErrorForDisplay} from "../../utils/resolveErrorMessage";
import {useTheme} from "@mui/material/styles";
import {useKeycloak} from "@react-keycloak/web";
import {toast} from "react-toastify";
import {DescriptionOutlined, FileUploadOffRounded, FilterListRounded, UploadRounded} from "@mui-symbols-material/w200";
import CircularProgress from "@mui/joy/CircularProgress";
import FileList, {FileListItem} from "../FileList";
import {buildNodeImageTarget, nodeTypeLabelForSentence} from "../../hooks/useNodeImage";
import {getInstalledBaseNodeTypeName} from "../../utils/getInstalledBaseNodeTypeName";


interface Props {
    systemId: string;

}

const SystemFileManagement = ({ systemId }: Props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const { keycloak } = useKeycloak();

    const systemFilesQuery = useQuery(SystemFiles, {
        variables: {
            id: systemId,
        }
    })

    const [addFilesToInstalledBaseNodeMutation] = useMutation(AddFilesToInstalledBaseNode)

    const [dropzoneKey, setDropzoneKey] = useState(0);

    const [viewMode, setViewMode] = useState<FileListViewMode>(() => loadFileListViewMode());

    const handleViewChange = (_event: React.MouseEvent<HTMLElement>, newMode: FileListViewMode | null) => {
        if (!newMode) return; // ignore deselect → keep one option active
        setViewMode(newMode);
        saveFileListViewMode(newMode);
    };

    const installedBaseNodeFiles = _.get(systemFilesQuery, "data.system.installedBaseNode.fileList.installedBaseNodeFiles", []) ?? []
    const installedBaseNodeId = _.get(systemFilesQuery, "data.system.installedBaseNode.id", null)
    const isLoading = systemFilesQuery?.loading;
    // "Use as system image" on image files — needs key/title for the update mutation.
    const fileNode = _.get(systemFilesQuery, "data.system.installedBaseNode");
    const nodeImageTarget = buildNodeImageTarget(
        fileNode,
        nodeTypeLabelForSentence(fileNode ? getInstalledBaseNodeTypeName(fileNode.nodeType, t) : undefined, i18n.language)
    ) ?? undefined;

    const systemFiles: FileListItem[] = useMemo(() => {
        return installedBaseNodeFiles
            .filter(Boolean)
            .map((f: any) => ({
                id: f.id,
                fileId: f.file?.id ?? null,
                filename: f.filename,
                basename: f.file?.basename ?? '',
                extension: f.file?.extension ?? null,
                comment: f.comment ?? null,
                supportsComment: true,
                category: f.category ?? t("User files"),
                insert: f.insert ?? null,
                contentType: f.file?.contentType ?? '',
                size: f.file?.size ?? 0,
                link: f.file?.link ?? '',
                thumbnailLink: f.file?.thumbnailLink ?? null,
                previewLink: f.file?.previewLink ?? null,
                language: f.file?.language ?? null,
                access: f.access ?? { read: false, write: false, delete: false },
                kind: 'installedBaseNode',
            }));
    }, [installedBaseNodeFiles, t]);

    const uploadFiles = async (files: File[]) => {
        if (files && files.length > 0) {
            try {
                // upload the files to the file microservice (POST /file)
                const uploadedFiles = await uploadFilesRequest(files, keycloak.token);

                const mutationInput = uploadedFiles.map((file) => {
                    return {
                        filename: file.filename,
                        fileServiceUuid: file.fileId,
                    }
                })

                // add the uploaded files to the product
                const res = await addFilesToInstalledBaseNodeMutation({
                    variables: {
                        installedBaseNodeId: installedBaseNodeId,
                        input: mutationInput
                    },
                    refetchQueries: ["SystemFiles"]
                })

                if (res?.data?.addFilesToInstalledBaseNode?.length > 0) {
                    toast.success(t("File(s) uploaded succesfully."));
                }
            } catch (err) {
                toast.error(resolveErrorMessage(err, t))
            } finally {
                setDropzoneKey((k) => k + 1);
            }
        }
    }

    return (

        <Box sx={{height: '100%', display: 'flex', width: '100%', maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'hidden',}}>
            <WidgetBox
                title={t("Documents")}
                titleIcon={<DescriptionOutlined sx={{color: '#333', fontSize: 28}} />}
                sx={{minHeight: '545px', flex: { md: 2, lg: 4 }, minWidth: 0, border: 'none', pl: '13px', pr: '0', pt: '0', pb: '0'}}
            >
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '300px' }}>
                        <CircularProgress variant="soft" />
                    </Box>
                ) : systemFilesQuery.error ? (

                    <Error errorObject={sanitizeErrorForDisplay(systemFilesQuery.error, t)}/>

                ) : (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', minHeight: '32px' }}>
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                size="small"
                                onChange={handleViewChange}
                                aria-label={t("View")}
                                sx={{flexShrink: 0, height: '32px'}}
                            >
                                <ToggleButton value="list" aria-label={t("List view")} sx={{px: '8px', py: 0}}>
                                    <Tooltip title={t("List view")}>
                                        <ViewListRounded sx={{fontSize: 20}} />
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton value="grid" aria-label={t("Tile view")} sx={{px: '8px', py: 0}}>
                                    <Tooltip title={t("Tile view")}>
                                        <GridViewRounded sx={{fontSize: 20}} />
                                    </Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        <FileList files={systemFiles} viewMode={viewMode} nodeImageTarget={nodeImageTarget} />
                    </>
                )}
            </WidgetBox>
            <Box sx={{
                display:'flex', flexDirection:'column', flex: 1, justifyContent: 'space-between', maxWidth:'350px', minWidth: '250px',
                gap: '13px', pl: '21px', pr:'8px', pt:'13px', borderLeft: "1px solid #cccccc", ml: '12px'
            }}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: '8px', pb: '21px'}}>

                    <FilterListRounded sx={{width: '20px', height: '20px'}}/>
                    <Typography level = "titleMedium">
                        {t("Filter")}
                    </Typography>
                </Box>


                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', aspectRatio: '1/1'}}>
                    {
                        installedBaseNodeId ? (
                            <DropzoneArea
                                key={dropzoneKey}
                                theme={theme}
                                fileObjects={[]}
                                onChange={(loadedFiles: File[]) => {
                                    uploadFiles(loadedFiles)
                                }}
                                onDelete={() => {}}
                                filesLimit={10}
                                maxFileSize={999999999}
                                showPreviewsInDropzone={false}
                                showFileNames={true}
                                dropzoneText={t("Click here or drag files to this area to upload.")}
                                dropzoneClass={"asset-file-management-file-upload-dropzone"}
                                dropzoneParagraphClass={"custom-file-upload-textbox"}
                                showAlerts={['error']}
                                Icon={UploadRounded}
                                classes={{
                                    text: "asset-file-management-dropzone-text",
                                    root: "dropzone-root",
                                    icon: "asset-file-management-dropzone-icon"
                                }}
                            />
                        ):(
                            <Box sx={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                textAlign: 'center', aspectRatio: '1/1', border: '1px dashed #cccccc', borderRadius:'4px', gap: '21px', p: '21px'}}>
                                <Typography level="titleMediumLight">
                                    {t("Please install this system to add files.")}
                                </Typography>
                                <FileUploadOffRounded sx={{ width: 55, height: 55}}/>
                            </Box>
                        )
                    }
                </Box>

            </Box>


        </Box>

    );
};

export default SystemFileManagement;
